import React, { useCallback, useContext, useMemo } from 'react';
import { Badge, Box } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';
import useUrlParams from 'hooks/useUrlParams';
import { ArchivedManualsContext } from '../context';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import ManualHistoryTabContent from '../List';
import LegacyArchivedManualsProvider from 'views/App/Portal/Policy/LegacyArchivedManuals/context';
import LegacyKMSHistoryTabContent from 'views/App/Portal/Policy/LegacyArchivedManuals/List';

type ManualHistoryUrlParams = {
  tab?: string;
};

const TAB_MANUAL_HISTORY = 'manual-history';
const TAB_LEGACY = 'legacy';

const defaultParams: ManualHistoryUrlParams = {
  tab: TAB_MANUAL_HISTORY,
};

const ManualHistoryPage: React.FC = () => {
  const { manualsQuery, legacyManualsQuery } = useContext(ArchivedManualsContext);
  const [params, setParams] = useUrlParams(defaultParams);

  const manualHistoryCount = manualsQuery.data?.pages[0]?.count ?? 0;
  const legacyCount = legacyManualsQuery.data?.pages[0]?.count ?? 0;
  const showLegacyTab = legacyCount > 0;
  const bothQueriesReady =
    !manualsQuery.isLoading && !legacyManualsQuery.isLoading;
  const totalCount = manualHistoryCount + legacyCount;
  const showHeaderBadge =
    bothQueriesReady && totalCount > 0;

  const activeTab =
    params.tab === TAB_LEGACY && showLegacyTab ? TAB_LEGACY : TAB_MANUAL_HISTORY;

  const updateParams = useCallback(
    (updatedParams: ManualHistoryUrlParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const tabs = useMemo(() => {
    const result: Record<
      string,
      { label: string; value: string; count?: number; component: React.ReactElement }
    > = {
      [TAB_MANUAL_HISTORY]: {
        label: t('policy.manualHistory.tabs.manualHistory'),
        value: TAB_MANUAL_HISTORY,
        count: manualHistoryCount > 0 ? manualHistoryCount : undefined,
        component: <ManualHistoryTabContent />,
      },
    };
    if (showLegacyTab) {
      result[TAB_LEGACY] = {
        label: t('policy.manualHistory.tabs.legacyKms'),
        value: TAB_LEGACY,
        count: legacyCount,
        component: (
          <LegacyArchivedManualsProvider>
            <LegacyKMSHistoryTabContent />
          </LegacyArchivedManualsProvider>
        ),
      };
    }
    return result;
  }, [manualHistoryCount, legacyCount, showLegacyTab]);

  return (
    <CommonLayout
      headerProps={{
        title: t('policy.manualHistory.title'),
        subtitle: t('policy.manualHistory.subtitle'),
        titleBadge:
          showHeaderBadge ? (
            <Badge
              variant='inline'
              badgeContent={totalCount}
              color='neutral'
              sx={{ textTransform: 'uppercase' }}
            />
          ) : undefined,
      }}
      tabs={{
        items: Object.entries(tabs).map(([key, tab]) => ({
          label: tab.label,
          value: key,
          count: tab.count,
        })),
        value: activeTab,
        onChange: (_event: React.SyntheticEvent, newValue: string | number) =>
          updateParams({ tab: newValue.toString() }),
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: '1fr', minHeight: 0 }}>
        {tabs[activeTab]?.component}
      </Box>
    </CommonLayout>
  );
};

export default ManualHistoryPage;
